import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "../data/business";
import { Logo } from "./Logo";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      {/* Announcement bar — editable via BUSINESS.announcement */}
      <div className="bg-ink text-white/80 text-center text-xs sm:text-sm py-2 px-4 font-body" data-testid="announcement-bar">
        {BUSINESS.announcement}
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-ink/90 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.35)] border-b border-copper/20"
            : "bg-ink border-b border-white/5"
        }`}
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-all duration-500 ${scrolled ? "h-14" : "h-[76px]"}`}>
          <Logo />
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-medium tracking-wide transition-colors duration-300 hover:text-copper-hover ${
                    isActive ? "text-copper" : "text-white/75"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/book"
              data-testid="nav-book-btn"
              className="hidden sm:inline-flex items-center rounded-full bg-copper px-5 py-2.5 text-[13px] font-semibold text-ink font-display tracking-wide transition-all duration-300 hover:bg-copper-hover hover:-translate-y-px"
            >
              Book Appointment
            </Link>
            <button
              type="button"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden text-white p-2"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink pt-28 px-8 pb-10 flex flex-col lg:hidden"
            data-testid="mobile-menu"
          >
            <nav aria-label="Mobile" className="flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-3 font-display font-bold text-3xl border-b border-white/5 ${isActive ? "text-copper" : "text-white"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-3">
              <Link to="/book" data-testid="mobile-menu-book-btn" className="rounded-full bg-copper text-ink text-center font-display font-bold py-4">
                Book Appointment
              </Link>
              <a href={BUSINESS.phoneHref} data-testid="mobile-menu-call-btn" className="rounded-full border border-white/20 text-white text-center font-display font-semibold py-4 inline-flex items-center justify-center gap-2">
                <Phone size={17} /> Call Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
